/**
 * Persistance locale du coffre : interface + implementation memoire.
 *
 * Meme convention que `apps/server` (`accounts-store.ts`, `session-store.ts`) :
 * la logique applicative (`vault-store.ts`) ne connait que cette interface,
 * jamais IndexedDB directement. Ce qui est stocke ici est EXACTEMENT ce que le
 * serveur souverain pourrait voir : `EncryptedVaultItem`/`EncryptedVaultFolder`
 * (ciphertext opaque + metadonnees claires) et l'enveloppe de compte (aucun
 * secret). `IndexedDbVaultRepository` (`indexeddb-repository.ts`) est
 * l'implementation navigateur ; `MemoryVaultRepository` sert aux tests et de
 * repli si IndexedDB est indisponible.
 */
import type { EncryptedVaultAttachment, EncryptedVaultFolder, EncryptedVaultItem } from '@klavox/vault-model';
import type { AccountEnvelope, PasskeyUnlockRecord } from './types.js';

export interface VaultRepository {
  loadAccountEnvelope(): Promise<AccountEnvelope | null>;
  saveAccountEnvelope(envelope: AccountEnvelope): Promise<void>;
  clearAccountEnvelope(): Promise<void>;

  /**
   * Passkeys autorisees a deverrouiller ce coffre sur CET appareil (R12).
   * Propres a l'appareil : ni synchronisees, ni exportees. La liste entiere est
   * reecrite a chaque changement -- enroler et revoquer restent la meme ecriture.
   */
  listPasskeyUnlocks(): Promise<PasskeyUnlockRecord[]>;
  savePasskeyUnlocks(records: readonly PasskeyUnlockRecord[]): Promise<void>;

  /**
   * Identifiant d'appareil (mode voyage, R18) : opaque, non secret, propre a
   * CET appareil -- ni synchronise, ni exporte. Il sert au serveur a refuser
   * la desactivation du mode voyage demandee par l'appareil qui l'a armee.
   */
  loadDeviceId(): Promise<string | null>;
  saveDeviceId(deviceId: string): Promise<void>;

  listItems(): Promise<EncryptedVaultItem[]>;
  putItem(item: EncryptedVaultItem): Promise<void>;
  putItems(items: readonly EncryptedVaultItem[]): Promise<void>;

  listFolders(): Promise<EncryptedVaultFolder[]>;
  putFolder(folder: EncryptedVaultFolder): Promise<void>;
  putFolders(folders: readonly EncryptedVaultFolder[]): Promise<void>;

  /** Pieces jointes chiffrees (tombstones compris) ; limitees a une entree si `itemId` est fourni. */
  listAttachments(itemId?: string): Promise<EncryptedVaultAttachment[]>;
  putAttachments(attachments: readonly EncryptedVaultAttachment[]): Promise<void>;

  /**
   * Retire DEFINITIVEMENT des enregistrements du stockage LOCAL, sans
   * tombstone (mode voyage, R18) : ce n'est pas une suppression du coffre --
   * le serveur et les autres appareils les gardent, et la synchro les
   * rapatriera a la desactivation. Un tombstone, lui, les detruirait partout.
   */
  deleteRecords(ids: {
    readonly items?: readonly string[];
    readonly folders?: readonly string[];
    readonly attachments?: readonly string[];
  }): Promise<void>;

  /** Vide entierement le coffre local (items, dossiers, pieces jointes, enveloppe). Verrouillage destructif ("effacer cet appareil"). */
  clearAll(): Promise<void>;
}

export class MemoryVaultRepository implements VaultRepository {
  #envelope: AccountEnvelope | null = null;
  #passkeyUnlocks: PasskeyUnlockRecord[] = [];
  #deviceId: string | null = null;
  readonly #items = new Map<string, EncryptedVaultItem>();
  readonly #folders = new Map<string, EncryptedVaultFolder>();
  readonly #attachments = new Map<string, EncryptedVaultAttachment>();

  async loadAccountEnvelope(): Promise<AccountEnvelope | null> {
    return this.#envelope;
  }

  async saveAccountEnvelope(envelope: AccountEnvelope): Promise<void> {
    this.#envelope = envelope;
  }

  async clearAccountEnvelope(): Promise<void> {
    this.#envelope = null;
  }

  async listPasskeyUnlocks(): Promise<PasskeyUnlockRecord[]> {
    return [...this.#passkeyUnlocks];
  }

  async savePasskeyUnlocks(records: readonly PasskeyUnlockRecord[]): Promise<void> {
    this.#passkeyUnlocks = [...records];
  }

  async loadDeviceId(): Promise<string | null> {
    return this.#deviceId;
  }

  async saveDeviceId(deviceId: string): Promise<void> {
    this.#deviceId = deviceId;
  }

  async listItems(): Promise<EncryptedVaultItem[]> {
    return Array.from(this.#items.values());
  }

  async putItem(item: EncryptedVaultItem): Promise<void> {
    this.#items.set(item.id, item);
  }

  async putItems(items: readonly EncryptedVaultItem[]): Promise<void> {
    for (const item of items) this.#items.set(item.id, item);
  }

  async listFolders(): Promise<EncryptedVaultFolder[]> {
    return Array.from(this.#folders.values());
  }

  async putFolder(folder: EncryptedVaultFolder): Promise<void> {
    this.#folders.set(folder.id, folder);
  }

  async putFolders(folders: readonly EncryptedVaultFolder[]): Promise<void> {
    for (const folder of folders) this.#folders.set(folder.id, folder);
  }

  async listAttachments(itemId?: string): Promise<EncryptedVaultAttachment[]> {
    const all = Array.from(this.#attachments.values());
    return itemId === undefined ? all : all.filter((attachment) => attachment.itemId === itemId);
  }

  async putAttachments(attachments: readonly EncryptedVaultAttachment[]): Promise<void> {
    for (const attachment of attachments) this.#attachments.set(attachment.id, attachment);
  }

  async deleteRecords(ids: {
    readonly items?: readonly string[];
    readonly folders?: readonly string[];
    readonly attachments?: readonly string[];
  }): Promise<void> {
    for (const id of ids.items ?? []) this.#items.delete(id);
    for (const id of ids.folders ?? []) this.#folders.delete(id);
    for (const id of ids.attachments ?? []) this.#attachments.delete(id);
  }

  async clearAll(): Promise<void> {
    this.#envelope = null;
    this.#passkeyUnlocks = [];
    this.#deviceId = null;
    this.#items.clear();
    this.#folders.clear();
    this.#attachments.clear();
  }
}
